import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text} from '@chakra-ui/react'
import {useIntl, defineMessages} from 'react-intl'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

import {getGDIHideStyles, mergeGDISx} from '../../utils/gdi-styles'
import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../../utils/contentRenderer'
import {useLoyaltyData} from '../../../../../contexts/loyalty-data-context'
import {useBrand} from '../../../../../contexts/brand-context'
import {useMyRewardsStyles} from '../../../../../pages/my-rewards/styles/myRewardsStyles'
import {
    GDIRewardTileProvider,
    REWARD_TYPE_EXCLUSIVE_REWARDS
} from '../../utils/gdi-reward-tile-context'
import {REWARD_TYPE_EARN_ACTIONS, computeEarnActionCompleted} from './earn-action-completion'

const messages = defineMessages({
    completed: {
        id: 'gdi.reward_tile.completed',
        defaultMessage: 'Completed'
    }
})

/**
 * GDIRewardTileBlock
 *
 * Thin wrapper around a `Content` area authored in the CMS for a single
 * reward tile (e.g. Exclusive Rewards, Earn Actions). The block itself
 * doesn't render any visual chrome — it surfaces the reward `Type` and
 * `ReferenceID` as `data-*` attributes (useful for analytics / targeted
 * styling) and delegates rendering of the inner blocks (typically a
 * single `GDIContentTileBlock`) to the standard content switch.
 *
 * When `Type === "Exclusive Rewards"`, rendering is additionally gated on
 * the customer's loyalty entitlements: the tile is only shown when its
 * `ReferenceID` matches one of the `tile-content-id` values returned by
 * the Salesforce Loyalty `sync-data` API (e.g. `bdaygift25-tile`). While
 * loyalty data is still loading, or when the customer has no matching
 * reward, the tile is hidden.
 *
 * When `Type === "Earn Actions"`, the block evaluates the customer /
 * loyalty-data condition keyed by `ReferenceID` (see
 * `earn-action-completion.js`) and appends a "Completed" badge below
 * the authored content once the action is satisfied. The wrapper also
 * gains `data-reward-completed="true"` so authored CSS can react to it.
 */
const GDIRewardTileBlock = ({
    HideBlockOn,
    Type,
    ReferenceID,
    GDIRewardTileBlockContent
}) => {
    const {loyaltyData, isReady} = useLoyaltyData()
    const {data: customer} = useCurrentCustomer()
    const {brandId} = useBrand()
    const intl = useIntl()
    /* Theme-driven styles for the reward tile (e.g. the "Completed" badge).
     * Sourced from `theme/components/project/my-rewards/gdiRewardTileBlock.js`
     * and surfaced via `MyRewardsStylesProvider` in the my-rewards page. Falls
     * back to an empty object so the component still renders if mounted
     * outside that provider. */
    const myRewardsStyles = useMyRewardsStyles() || {}

    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    const blocks = getAllExpandedFromContentArea(GDIRewardTileBlockContent)
    if (blocks.length === 0) return null

    const rewards = Array.isArray(loyaltyData?.rewards) ? loyaltyData.rewards : []
    const matchedReward = rewards.find((reward) => reward?.['tile-content-id'] === ReferenceID)

    if (Type === REWARD_TYPE_EXCLUSIVE_REWARDS) {
        // Wait until we know whether the customer has this reward before
        // rendering anything — prevents flashing tiles to ineligible users.
        if (!isReady) return null
        if (!matchedReward) return null
    }

    /* Earn Actions tiles: derive the customer's completion status for the
     * action keyed by `ReferenceID` and render a "Completed" badge below the
     * authored tile content when satisfied. See `earn-action-completion.js`.
     * `null` means the answer is not yet known (e.g. loyalty data still
     * loading); render nothing in that case to avoid a flash of the badge. */
    const isEarnActions = Type === REWARD_TYPE_EARN_ACTIONS
    const earnActionCompleted = isEarnActions
        ? computeEarnActionCompleted({referenceID: ReferenceID, customer, loyaltyData, brandId})
        : false
    const showCompletedBadge =
        isEarnActions && earnActionCompleted === true && customer?.isRegistered

    /* Reward tile icons (e.g. "Birthday Gift", "Free Shipping") are always authored as
     * small 48px icons. That cap currently only exists as an authored `MaxWidth` on
     * `GDIImageBlockStylesPerViewType` — if an editor picks a very wide image without
     * also setting that field, the icon has nothing stopping it from rendering at its
     * full natural width. Enforce the 48px ceiling here in code as a safety net so the
     * icon never grows beyond the intended size regardless of CMS authoring. */
    const iconMaxWidthSx = {
        '& [data-block-type="GDIImageBlock"]': {
            maxWidth: '48px !important'
        }
    }

    return (
        <GDIRewardTileProvider
            type={Type}
            referenceID={ReferenceID}
            voucherCode={matchedReward?.voucherCode}
            startDate={matchedReward?.startDate}
            endDate={matchedReward?.endDate}
        >
            <Box
                data-block-type="GDIRewardTileBlock"
                data-reward-type={Type || undefined}
                data-reward-reference-id={ReferenceID || undefined}
                data-reward-completed={showCompletedBadge ? 'true' : undefined}
                sx={mergeGDISx(iconMaxWidthSx, hideStyles.sx)}
            >
                {blocks.map((block, idx) => (
                    <React.Fragment key={block?.ContentLink?.GuidValue || `reward-tile-${idx}`}>
                        {renderLayoutsSwitch(block) || renderComponentsSwitch(block)}
                    </React.Fragment>
                ))}
                {showCompletedBadge && (
                    <Box {...myRewardsStyles.badgeContainer}>
                        <Text
                            data-reward-completed-badge="true"
                            {...myRewardsStyles.rewardTileCompletedBadge}
                        >
                            {intl.formatMessage(messages.completed)}
                        </Text>
                    </Box>
                )}
            </Box>
        </GDIRewardTileProvider>
    )
}

GDIRewardTileBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    Type: PropTypes.string,
    ReferenceID: PropTypes.string,
    GDIRewardTileBlockContent: PropTypes.array,
    GDIRewardTileBlockAudienceTargeting: PropTypes.object
}

export default GDIRewardTileBlock
